<script setup lang="ts">
/**
 * CreateLinkModal — 1:1 порт docs/mockups/modal-create-link.html
 * (inbox-135).
 *
 * Секции мокапа (все 5):
 *   1. URL "Куда ведёт ссылка" → target_url
 *   2. Slug "Короткий код" → slug (optional)
 *   3. Title → title (optional)
 *   4. Tags → tags[] (optional, backend supports per LinkCreate.tags)
 *   5. UTM grid (source / medium / campaign / term / content) — backend
 *      не хранит UTM как отдельные поля, поэтому merge'им в target_url
 *      перед POST. UTM-term/content на mock-узком backend'е остаются
 *      доступны только через URL-инжект (не выделяемые в analytics).
 *
 * POST /api/v1/links через LinksApi.create → emit('created') →
 * modals.emit('create-link', link) → modals.close().
 */

import { computed, onMounted, ref, watch } from 'vue';
import { useSwipe } from '@vueuse/core';
import { LinksApi, ApiError, type Link } from '@/api/links';
import { CampaignsApi, type Campaign } from '@/api/campaigns';
import { useModalStore } from '@/stores/modal';
import UiField from '@/components/ui/UiField.vue';
import UiInput from '@/components/ui/UiInput.vue';
import UiSelect from '@/components/ui/UiSelect.vue';
import UiButton from '@/components/ui/UiButton.vue';

const modals = useModalStore();

// inbox-217 — список активных кампаний для optional привязки. Загружаем
// один раз на mount; пустой список / 404 → UiSelect остаётся с одной
// «Без кампании» опцией и не блокирует submit.
const campaigns = ref<Campaign[]>([]);
const campaignsLoading = ref(false);
const selectedCampaignId = ref<string>('');

const campaignOptions = computed(() => [
  { value: '', label: 'Без кампании' },
  ...campaigns.value.map((c) => ({ value: String(c.id), label: c.name })),
]);

async function loadCampaigns(): Promise<void> {
  campaignsLoading.value = true;
  try {
    campaigns.value = await CampaignsApi.list();
  } catch {
    // 404 / нет endpoint'а / 401 — оставляем пустой массив, UI gracefully
    // показывает только «Без кампании».
    campaigns.value = [];
  } finally {
    campaignsLoading.value = false;
  }
}

onMounted(() => {
  if (modals.activeKey === 'create-link') void loadCampaigns();
});

// inbox-202 — короткие домены. Список должен совпадать с backend
// SL_ALLOWED_REDIRECT_HOSTS (см. app/core/config.py).
const HOST_OPTIONS = [
  { value: 'justasecondgo.com', label: 'justasecondgo.com' },
  { value: 'hopw.cc', label: 'hopw.cc' },
  { value: 'vroo.cc', label: 'vroo.cc' },
  { value: 'gonew.click', label: 'gonew.click' },
];

const targetUrl = ref('');
const host = ref('justasecondgo.com');
const slug = ref('');
const title = ref('');
const tagsRaw = ref('');
const utmSource = ref('');
const utmMedium = ref('');
const utmCampaign = ref('');
const utmTerm = ref('');
const utmContent = ref('');
const utmOpen = ref(false);
const error = ref<string | null>(null);

const slugError = computed(() => {
  if (!slug.value) return '';
  if (!/^[A-Za-z0-9_-]{3,64}$/.test(slug.value)) {
    return '3–64 символа: латиница, цифры, дефис, подчёркивание';
  }
  return '';
});

// Сброс state'a когда модалку переоткрывают.
watch(
  () => modals.activeKey,
  (key) => {
    if (key !== 'create-link') return;
    targetUrl.value = '';
    host.value = 'justasecondgo.com';
    slug.value = '';
    title.value = '';
    tagsRaw.value = '';
    selectedCampaignId.value = '';
    utmSource.value = '';
    utmMedium.value = '';
    utmCampaign.value = '';
    utmTerm.value = '';
    utmContent.value = '';
    utmOpen.value = false;
    error.value = null;
    // Перезагружаем список — кампания могла быть создана между
    // открытиями модалки.
    void loadCampaigns();
    // Pre-fill из payload (например, открыли «+ Создать ссылку» из
    // CampaignDetailView — там campaignId уже известен).
    const payload = modals.payload as { campaignId?: number } | null;
    if (payload?.campaignId != null) {
      selectedCampaignId.value = String(payload.campaignId);
    }
  },
);

function buildTargetUrl(): string {
  let url = targetUrl.value.trim();
  if (!url) return '';
  if (!/^https?:\/\//i.test(url)) url = 'https://' + url;
  const utm: Record<string, string> = {};
  if (utmSource.value.trim()) utm['utm_source'] = utmSource.value.trim();
  if (utmMedium.value.trim()) utm['utm_medium'] = utmMedium.value.trim();
  if (utmCampaign.value.trim()) utm['utm_campaign'] = utmCampaign.value.trim();
  if (utmTerm.value.trim()) utm['utm_term'] = utmTerm.value.trim();
  if (utmContent.value.trim()) utm['utm_content'] = utmContent.value.trim();
  if (Object.keys(utm).length === 0) return url;
  try {
    const u = new URL(url);
    for (const [k, v] of Object.entries(utm)) u.searchParams.set(k, v);
    return u.toString();
  } catch {
    return url;
  }
}

async function submit() {
  error.value = null;
  if (slugError.value) {
    error.value = slugError.value;
    return;
  }
  const url = buildTargetUrl();
  if (!url) {
    error.value = 'Введите URL';
    return;
  }
  try {
    new URL(url);
  } catch {
    error.value = 'Невалидный URL';
    return;
  }

  modals.setPending(true);
  try {
    const tags = tagsRaw.value
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    const created: Link = await LinksApi.create({
      target_url: url,
      host: host.value,
      slug: slug.value.trim() || undefined,
      title: title.value.trim() || null,
      tags,
      // inbox-217 — campaign optional, пустая строка = null.
      campaign_id: selectedCampaignId.value ? Number(selectedCampaignId.value) : null,
    });
    modals.emit('create-link', created);
    modals.close();
  } catch (e) {
    error.value = e instanceof ApiError ? e.message : (e as Error).message;
  } finally {
    modals.setPending(false);
  }
}

function onClose() {
  if (!modals.pending) modals.close();
}

// inbox-290 — bottom-sheet swipe-down close на мобильном. useSwipe
// слушает touch на sheetRef. При свайпе вниз >100px (lengthY < 0 при
// движении пальца вверх; для down — direction === 'down' и lengthY > 0
// в @vueuse/core 11.x), закрываем модалку.
const sheetRef = ref<HTMLElement | null>(null);
const { direction, lengthY } = useSwipe(sheetRef, {
  threshold: 50,
  onSwipeEnd() {
    if (direction.value === 'down' && Math.abs(lengthY.value) > 100) {
      onClose();
    }
  },
});
</script>

<template>
  <!-- inbox-290 — mobile bottom-sheet: на <sm приклеена к низу с handle
       и swipe-down close, на >=sm центрированный модал как раньше.
       ModalRoot уже делает Teleport to body + Transition, дополнительный
       Teleport не нужен. -->
  <div
    class="sheet-overlay"
    role="presentation"
    @click.self="onClose"
  >
    <div
      ref="sheetRef"
      class="sheet-card"
      role="dialog"
      aria-modal="true"
      aria-label="Новая короткая ссылка"
    >
      <!-- Handle: только на мобильном. Также служит handle-областью для swipe-down. -->
      <div class="sheet-handle" aria-hidden="true">
        <div class="sheet-handle-bar" />
      </div>

      <header class="sheet-head">
        <div class="sheet-titles">
          <h2 class="sheet-title">Новая короткая ссылка</h2>
          <p class="sheet-sub">URL, кастомный slug, теги и UTM — за один шаг.</p>
        </div>
        <button
          type="button"
          class="sheet-close"
          :disabled="modals.pending"
          aria-label="Закрыть"
          @click="onClose"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>
      </header>

      <div class="sheet-body">
        <form id="create-link-form" @submit.prevent="submit">
      <!-- 1. URL -->
      <UiField
        label="Куда ведёт ссылка"
        for="cl-target"
        :error="error && !targetUrl ? 'Введите URL' : ''"
        required
      >
        <UiInput
          id="cl-target"
          v-model="targetUrl"
          type="text"
          placeholder="https://example.com/some/long/path"
          autocomplete="url"
          :disabled="modals.pending"
          autofocus
          hint="Если протокол не указан — добавим https:// автоматически."
        />
      </UiField>

      <!-- 1b. Host (inbox-202) — короткий домен. -->
      <UiField
        label="Домен"
        for="cl-host"
        hint="Какой короткий домен использовать."
      >
        <UiSelect
          v-model="host"
          :options="HOST_OPTIONS"
          :disabled="modals.pending"
          name="host"
        />
      </UiField>

      <!-- 2. Slug -->
      <UiField
        label="Короткий код"
        for="cl-slug"
        :error="slugError"
        hint="Опционально. Можно оставить пустым — сгенерируем сами."
      >
        <UiInput
          id="cl-slug"
          v-model="slug"
          placeholder="my-promo"
          :disabled="modals.pending"
        />
      </UiField>

      <!-- 3. Title -->
      <UiField
        label="Название"
        for="cl-title"
        hint="Для удобного поиска в списке ссылок."
      >
        <UiInput
          id="cl-title"
          v-model="title"
          placeholder="Промо-страница Q2"
          :disabled="modals.pending"
        />
      </UiField>

      <!-- 3b. Campaign (inbox-217) — optional dropdown. -->
      <UiField
        label="Кампания"
        for="cl-campaign"
        :hint="campaignsLoading
          ? 'Загружаем список…'
          : campaigns.length === 0
            ? 'Кампаний пока нет — создайте на /campaigns и они появятся здесь.'
            : 'Опционально. Можно привязать ссылку к существующей кампании.'"
      >
        <UiSelect
          v-model="selectedCampaignId"
          :options="campaignOptions"
          :disabled="modals.pending || campaignsLoading"
          name="campaign_id"
        />
      </UiField>

      <!-- 4. Tags -->
      <UiField
        label="Теги"
        for="cl-tags"
        hint="Через запятую. Например: q2, vk, performance"
      >
        <UiInput
          id="cl-tags"
          v-model="tagsRaw"
          placeholder="q2, vk, performance"
          :disabled="modals.pending"
        />
      </UiField>

      <!-- 5. UTM grid (collapsed by default) -->
      <div class="utm-section">
        <button
          type="button"
          class="utm-toggle"
          :aria-expanded="utmOpen"
          :disabled="modals.pending"
          @click="utmOpen = !utmOpen"
        >
          <span>UTM-метки <span class="opt">— опционально</span></span>
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            :class="{ rotated: utmOpen }"
            aria-hidden="true"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        <div v-if="utmOpen" class="utm-grid">
          <UiField label="source" for="cl-utm-src">
            <UiInput
              id="cl-utm-src"
              v-model="utmSource"
              placeholder="vk"
              :disabled="modals.pending"
            />
          </UiField>
          <UiField label="medium" for="cl-utm-med">
            <UiInput
              id="cl-utm-med"
              v-model="utmMedium"
              placeholder="cpc"
              :disabled="modals.pending"
            />
          </UiField>
          <UiField label="campaign" for="cl-utm-cam">
            <UiInput
              id="cl-utm-cam"
              v-model="utmCampaign"
              placeholder="spring-2026"
              :disabled="modals.pending"
            />
          </UiField>
          <UiField label="term" for="cl-utm-term" hint="опционально">
            <UiInput
              id="cl-utm-term"
              v-model="utmTerm"
              placeholder="keyword"
              :disabled="modals.pending"
            />
          </UiField>
          <UiField label="content" for="cl-utm-cont" hint="опционально">
            <UiInput
              id="cl-utm-cont"
              v-model="utmContent"
              placeholder="banner-top"
              :disabled="modals.pending"
            />
          </UiField>
        </div>
      </div>

      <p v-if="error && targetUrl" class="form-error" role="alert">{{ error }}</p>
        </form>
      </div>

      <footer class="sheet-foot">
        <UiButton
          variant="ghost"
          :disabled="modals.pending"
          @click="onClose"
        >Отмена</UiButton>
        <UiButton
          type="submit"
          form="create-link-form"
          :loading="modals.pending"
        >Создать ссылку</UiButton>
      </footer>
    </div>
  </div>
</template>

<style scoped>
/* inbox-290 — bottom-sheet container.
   На <sm: card прижата к низу, rounded-top, handle visible, swipe-down close
   На >=sm (640px): card центрированный модал с rounded углами как раньше */
.sheet-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(10, 13, 18, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}
.sheet-card {
  background: var(--bg-card);
  border: 1px solid var(--border-strong);
  border-top: 1px solid var(--border-strong);
  width: 100%;
  max-width: 100%;
  max-height: 92vh;
  border-radius: 16px 16px 0 0;
  display: flex;
  flex-direction: column;
  box-shadow: 0 -8px 40px rgba(0, 0, 0, 0.5);
  padding-bottom: env(safe-area-inset-bottom);
  touch-action: pan-y;
}
.sheet-handle {
  display: flex;
  justify-content: center;
  padding: 8px 0 2px;
  cursor: grab;
}
.sheet-handle-bar {
  width: 44px;
  height: 5px;
  border-radius: 999px;
  background: var(--fg-dim);
  opacity: 0.55;
}
.sheet-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 18px 12px;
  border-bottom: 1px solid var(--border);
}
.sheet-titles { flex: 1; min-width: 0; }
.sheet-title { font-size: 16px; font-weight: 600; letter-spacing: -0.01em; margin: 0; color: var(--fg); }
.sheet-sub { font-size: 13px; color: var(--fg-muted); margin: 4px 0 0; }
.sheet-close {
  width: 40px; height: 40px;
  display: grid; place-items: center;
  border-radius: 8px;
  color: var(--fg-muted);
  background: transparent;
  border: 0;
  cursor: pointer;
  flex-shrink: 0;
}
.sheet-close:hover:not(:disabled) { background: var(--bg-hover); color: var(--fg); }
.sheet-close:disabled { opacity: 0.5; cursor: not-allowed; }
.sheet-body {
  padding: 14px 18px;
  overflow-y: auto;
  flex: 1;
}
.sheet-foot {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  border-top: 1px solid var(--border);
  background: var(--bg);
  justify-content: flex-end;
  flex-wrap: wrap;
}

@media (min-width: 640px) {
  .sheet-overlay {
    align-items: center;
    padding: 16px;
  }
  .sheet-card {
    max-width: 720px;
    max-height: 90vh;
    border-radius: 14px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.45);
  }
  .sheet-handle { display: none; }
  .sheet-head { padding: 18px 24px 14px; }
  .sheet-title { font-size: 18px; }
  .sheet-body { padding: 18px 24px; }
  .sheet-foot { padding: 14px 24px; flex-wrap: nowrap; }
}

.utm-section {
  margin-bottom: 14px;
}

.utm-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0 14px;
  min-height: 40px;
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: var(--fg);
  cursor: pointer;
  font-family: inherit;
}

.utm-toggle:hover:not(:disabled) {
  background: var(--bg-hover);
}

.utm-toggle:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.utm-toggle .opt {
  font-weight: 400;
  color: var(--fg-muted);
}

.utm-toggle svg {
  transition: transform 0.18s;
}

.utm-toggle svg.rotated {
  transform: rotate(180deg);
}

.utm-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 12px;
  padding: 14px;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 7px;
}

.utm-grid :deep(.field) {
  margin-bottom: 0;
}

.form-error {
  font-size: 12.5px;
  color: var(--danger);
  margin: 4px 0 0;
}

@media (max-width: 760px) {
  .utm-grid {
    grid-template-columns: 1fr;
  }
}
</style>
